import {
  Button,
  Text,
  Loader,
  Stack,
  Center,
  Box,
  Anchor,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import {
  displayErrorMessage,
  handleFetchingError,
} from "../shared/ErrorMessage";
import { initialize } from "../slices/form/login";
import { useEffect } from "react";
import { useAuth } from "../authentication/AuthProvider";
import { Link, useLocation } from "wouter";
import { Logo } from "../shared/components/Logo";
import {
  CreateUserRequest,
  useCreateUserMutation,
} from "../slices/api/usersApi";
import { getRecaptchaToken } from "../shared/recaptcha";
import { formatFullName } from "../shared/utility";
import { getLinkToVentHome } from "../shared/links";
import { useGetGuestInfoByGuidQuery } from "../slices/api/guestsApi";

interface Props {
  guestGuid: string;
}

const PersonalizedLogin = (props: Props) => {
  const { data } = useAppSelector((state: RootState) => state.loginForm);
  const { loading, user } = useAuth();
  const [_, navigate] = useLocation();
  const [createUser] = useCreateUserMutation();
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (data) return;
    dispatch(initialize({ email: "" }));
  }, []);
  const {
    data: guestData,
    isError: isErrorFetchingGuest,
    error: guestError,
  } = useGetGuestInfoByGuidQuery({
    guestGuid: props.guestGuid,
  });
  useEffect(() => {
    if (user && guestData) {
      navigate(getLinkToVentHome(guestData.ventId));
    }
  }, [user, guestData]);

  const handleClickSendEmail = async () => {
    if (data == null || guestData == null) {
      console.error("Data is undefined but should not be");
      return;
    }

    const token = await getRecaptchaToken();
    let params = "";
    params =
      "?redirect=" + encodeURIComponent(getLinkToVentHome(guestData.ventId));

    try {
      localStorage.setItem(
        "intendedUrl",
        window.location.origin + getLinkToVentHome(guestData.ventId)
      );
      const createUserRequest: CreateUserRequest = {
        email: guestData.email,
        firstName: guestData.firstName,
        lastName: guestData.lastName,
        url: window.location.origin + "/emailLoginCompletion" + params,
        recaptchaToken: token,
      };
      await createUser(createUserRequest);
      navigate("/waitForLoginEmail?source=personalizedLogin");
      window.localStorage.setItem("emailForSignIn", guestData.email);
    } catch (error) {
      displayErrorMessage("Login failed.");
      return;
    }
  };

  if (isErrorFetchingGuest) {
    handleFetchingError(guestError);
    return null;
  }

  if (loading || !data || !guestData) {
    return <Loader color="blue" />;
  }

  return (
    <Center style={{ height: "100vh" }}>
      <form onSubmit={(event) => event.preventDefault()}>
        <Stack align="center" gap="sm">
          <Box w={100}>
            <Logo />
          </Box>

          <Text size="md">{guestData.ventName}</Text>
          <Text size="xl">
            Personalized Login for{" "}
            {formatFullName(guestData.firstName, guestData.lastName)}
          </Text>

          <Text size="md" mt="xl" ml="xl" mr="xl">
            Click the button to receive an emailed login link so that you can
            RSVP (and automatically register with Early Bird RSVP if you are not
            yet registered).
          </Text>

          <Button onClick={handleClickSendEmail}>
            Send login link to {guestData.email}
          </Button>

          <Text size="md" ml="xl" mr="xl" mt="xl">
            If the above email is not yours, then you used someone else's
            personalized login link. Click{" "}
            <Anchor
              component={Link}
              href={getLinkToVentHome(guestData.ventId)}
              underline="hover"
            >
              here
            </Anchor>{" "}
            to login in with your own email.
          </Text>
        </Stack>
      </form>
    </Center>
  );
};

export default PersonalizedLogin;
